import React, { useState, useEffect } from 'react';
import api from '../api';

interface LogisticsOrder {
  id: number;
  order_number: string;
  external_order_id: string | null;
  customer_name: string | null;
  order_date: string;
  status: string;
  total_amount: number;
  channel: string | null;
  dc_number: string | null;
  dc_date: string | null;
  awb_number: string | null;
  shipping_status: string | null;
  ship_date: string | null;
  shipping_provider: string | null;
}

const LogisticsAnalytics: React.FC = () => {
  const [orders, setOrders] = useState<LogisticsOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterShippingStatus, setFilterShippingStatus] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterHasAwb, setFilterHasAwb] = useState('');
  const [filterHasDc, setFilterHasDc] = useState('');

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const res = await api.get('/logistics/orders', { params: { ...params, limit: 500 } });
      setOrders(res.data.orders || []);
    } catch (err) {
      console.error('Failed to fetch logistics data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Apply client-side filters
  const filteredOrders = orders.filter(o => {
    if (filterStatus && o.status !== filterStatus) return false;
    if (filterShippingStatus && o.shipping_status !== filterShippingStatus) return false;
    if (filterChannel && o.channel !== filterChannel) return false;
    if (filterProvider && o.shipping_provider !== filterProvider) return false;
    if (filterHasAwb === 'yes' && !o.awb_number) return false;
    if (filterHasAwb === 'no' && o.awb_number) return false;
    if (filterHasDc === 'yes' && !o.dc_number) return false;
    if (filterHasDc === 'no' && o.dc_number) return false;
    return true;
  });

  // Summary stats
  const stats = {
    total: filteredOrders.length,
    withAwb: filteredOrders.filter(o => o.awb_number).length,
    withDc: filteredOrders.filter(o => o.dc_number).length,
    booked: filteredOrders.filter(o => o.shipping_status === 'booked').length,
    inTransit: filteredOrders.filter(o => o.shipping_status === 'in_transit').length,
    outForDelivery: filteredOrders.filter(o => o.shipping_status === 'out_for_delivery').length,
    delivered: filteredOrders.filter(o => o.shipping_status === 'delivered').length,
    returned: filteredOrders.filter(o => o.shipping_status === 'returned').length,
    pending: filteredOrders.filter(o => !o.shipping_status && !o.awb_number).length,
    totalValue: filteredOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0),
  };

  const channels = Array.from(new Set(orders.map(o => o.channel).filter(Boolean)));
  const providers = Array.from(new Set(orders.map(o => o.shipping_provider).filter(Boolean)));

  const handleExportCSV = () => {
    setExporting(true);
    try {
      const headers = ['Order Number', 'External ID', 'Customer', 'Channel', 'Order Date', 'Order Status', 'DC Number', 'DC Date', 'AWB Number', 'Shipping Provider', 'Shipping Status', 'Ship Date', 'Amount'];
      const rows = filteredOrders.map(o => [
        o.order_number,
        o.external_order_id || '',
        o.customer_name || '',
        o.channel || '',
        o.order_date ? new Date(o.order_date).toLocaleDateString('en-IN') : '',
        o.status,
        o.dc_number || '',
        o.dc_date ? new Date(o.dc_date).toLocaleDateString('en-IN') : '',
        o.awb_number || '',
        o.shipping_provider || '',
        o.shipping_status || '',
        o.ship_date ? new Date(o.ship_date).toLocaleDateString('en-IN') : '',
        o.total_amount?.toFixed(2) || '0',
      ]);

      const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `logistics_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleClearFilters = () => {
    setFilterStatus('');
    setFilterShippingStatus('');
    setFilterChannel('');
    setFilterProvider('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterHasAwb('');
    setFilterHasDc('');
  };

  const formatDate = (d: string | null | undefined) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading logistics data...</div>;
  }

  return (
    <div>
      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Total Orders', value: stats.total, color: '#1e293b' },
          { label: 'AWB Generated', value: stats.withAwb, color: '#0d9488' },
          { label: 'DC Generated', value: stats.withDc, color: '#6366f1' },
          { label: 'Booked', value: stats.booked, color: '#f59e0b' },
          { label: 'In Transit', value: stats.inTransit, color: '#3b82f6' },
          { label: 'Out for Delivery', value: stats.outForDelivery, color: '#8b5cf6' },
          { label: 'Delivered', value: stats.delivered, color: '#10b981' },
          { label: 'Returned/RTO', value: stats.returned, color: '#ef4444' },
        ].map(tile => (
          <div key={tile.label} style={{ padding: '14px 16px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>{tile.label}</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: tile.color }}>{tile.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ padding: '14px 18px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>Order Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="allocated">Allocated</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>Shipping Status</label>
            <select value={filterShippingStatus} onChange={e => setFilterShippingStatus(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
              <option value="">All</option>
              <option value="booked">Booked</option>
              <option value="picked_up">Picked Up</option>
              <option value="in_transit">In Transit</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="returned">Returned</option>
              <option value="lost">Lost</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>Channel</label>
            <select value={filterChannel} onChange={e => setFilterChannel(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
              <option value="">All</option>
              {channels.map(ch => <option key={ch} value={ch!}>{ch}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>Provider</label>
            <select value={filterProvider} onChange={e => setFilterProvider(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
              <option value="">All</option>
              {providers.map(p => <option key={p} value={p!}>{p}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>AWB</label>
            <select value={filterHasAwb} onChange={e => setFilterHasAwb(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
              <option value="">All</option>
              <option value="yes">With AWB</option>
              <option value="no">Without AWB</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>DC</label>
            <select value={filterHasDc} onChange={e => setFilterHasDc(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
              <option value="">All</option>
              <option value="yes">With DC</option>
              <option value="no">Without DC</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>From</label>
            <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>To</label>
            <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
              style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
          </div>
          <button onClick={() => { fetchOrders(); }} style={{ padding: '6px 12px', fontSize: '12px', background: '#0d9488', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
            Apply
          </button>
          <button onClick={handleClearFilters} style={{ padding: '6px 12px', fontSize: '12px', background: 'white', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer' }}>
            Clear
          </button>
          <button onClick={handleExportCSV} disabled={exporting || filteredOrders.length === 0}
            style={{ padding: '6px 12px', fontSize: '12px', background: '#1e293b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, marginLeft: 'auto', opacity: filteredOrders.length === 0 ? 0.5 : 1 }}>
            {exporting ? '⏳ Exporting...' : `📥 Export CSV (${filteredOrders.length})`}
          </button>
        </div>
      </div>

      {/* Data table */}
      <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f8fafb' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Order #</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '11px' }}>Customer</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '11px' }}>Channel</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '11px' }}>Order Date</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '11px' }}>DC #</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '11px' }}>AWB #</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '11px' }}>Provider</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '11px' }}>Ship Date</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '11px' }}>Shipping Status</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '11px' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>No orders match the current filters.</td></tr>
              ) : (
                filteredOrders.map(o => (
                  <tr key={o.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 600 }}>{o.order_number}</td>
                    <td style={{ padding: '8px 12px' }}>{o.customer_name || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{o.channel || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{formatDate(o.order_date)}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '11px' }}>{o.dc_number || '-'}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '11px' }}>{o.awb_number || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{o.shipping_provider || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{formatDate(o.ship_date)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {o.shipping_status ? (
                        <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                          background: o.shipping_status === 'delivered' ? '#d4edda' : o.shipping_status === 'returned' ? '#f8d7da' : o.shipping_status === 'in_transit' ? '#cce5ff' : '#fff3cd',
                          color: o.shipping_status === 'delivered' ? '#155724' : o.shipping_status === 'returned' ? '#721c24' : o.shipping_status === 'in_transit' ? '#004085' : '#856404'
                        }}>
                          {o.shipping_status.replace(/_/g, ' ')}
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500 }}>₹{o.total_amount?.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer summary */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', background: '#f8fafb', display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748b' }}>
          <span>{filteredOrders.length} orders shown</span>
          <span>Total Value: <strong style={{ color: '#1e293b' }}>₹{stats.totalValue.toFixed(2)}</strong></span>
        </div>
      </div>
    </div>
  );
};

export default LogisticsAnalytics;
